import React, { useEffect, useState } from "react";
import Chart from "react-apexcharts";
import { RevenueIcon, SortDownArrow } from "../../assets/svg";
import Breadcrumb from "../../components/common/BreadCrumb";
import {
  DefaultPrimaryButton,
  ExportButton,
  SelectWithLabel,
} from "../../components/micro/input";
import CustomTable from "../../components/customTable/CustomTable";
import {
  getSalesReportList,
  getServicesReportList,
} from "../../redux/reports/slice";
import { useDispatch, useSelector } from "react-redux";
import RippleLoader from "../../components/loader/RippleLoader";
import ReactDatePicker from "react-datepicker";
import moment from "moment";
import ReactSelect from "react-select";
import { companiesList } from "../../redux/companies/slice";
import { useNavigate } from "react-router-dom";

const ServiceRequestReports = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear.toString());
  const [pageLimit, setPageLimit] = useState(10);
  const [pageCount, setPageCount] = useState(1);
  const [sortBy, setSortBy] = useState("");
  const [sortDirection, setSortDirection] = useState("desc");
  const [dateRange, setDateRange] = useState([null, null]);
  const [company, setCompany] = useState("");

  const DateRange =
    dateRange[0] && dateRange[1]
      ? `${moment(dateRange[0]).format("DD/MM/YYYY")} - ${moment(
          dateRange[1]
        ).format("DD/MM/YYYY")}`
      : "";

  // Selector
  const { getServicesReportListData, loading, companiesListData } = useSelector(
    (store) => ({
      getServicesReportListData:
        store?.salesReportData?.getServicesReportListStatus?.data,
      loading: store?.salesReportData?.loading,
      companiesListData: store?.companiesData?.companiesListStatus?.data?.data,
    })
  );

  useEffect(() => {
    dispatch(
      getServicesReportList({
        pageCount: pageCount,
        pageLimit: pageLimit,
        sort_by: sortBy,
        sort_direction: sortDirection === "asc" ? "asc" : "desc",
        date_range: DateRange || "",
        year: year || "",
        company: company?.label || "",
      })
    );
    dispatch(
      companiesList({
        search: "",
        pageLimit: "all",
        status: "",
        sort_by: "name",
        sort_direction: "desc",
        contract: "all",
      })
    );
  }, [pageCount, pageLimit, sortBy, sortDirection, DateRange, year, company]);

  const yearOptions = Array.from({ length: 5 }, (_, i) => ({
    label: (currentYear - i).toString(),
    value: (currentYear - i).toString(),
  }));

  const handleYearChange = (e) => {
    setYear(e.target.value);
  };

  const PieChartOptions = {
    chart: {
      type: "pie",
    },
    labels: ["Recurring", "1 Time"],
    legend: {
      position: "bottom",
      horizontalAlign: "center",
    },
    colors: ["#F15B29", "#2E2E2E"],
    dataLabels: {
      enabled: true,
      formatter: function (val, opts) {
        const count = opts.w.config.series[opts.seriesIndex];
        return `${count}`;
      },
      style: {
        fontSize: "14px",
        fontWeight: "bold",
        colors: ["#fff"],
      },
      dropShadow: {
        enabled: false,
      },
    },
  };

  const LineChartOptions = {
    chart: {
      type: "line",
      toolbar: { show: false },
    },
    stroke: {
      curve: "straight",
      width: 2,
    },
    xaxis: {
      categories: getServicesReportListData?.data?.service_request?.map(
        (item) => item?.month?.slice(0, 3)
      ),
    },
    colors: ["#F15B29", "#2E2E2E"],
    legend: {
      position: "bottom",
      horizontalAlign: "center",
    },
  };

  const pieSeries = [
    getServicesReportListData?.data?.recurrig_services || 0,
    getServicesReportListData?.data?.single_services || 0,
  ];

  const lineSeries = [
    {
      name: "Recurring",
      data: getServicesReportListData?.data?.service_request?.map(
        (item) => item?.recurrig_services || 0
      ),
    },
    {
      name: "1 Time",
      data: getServicesReportListData?.data?.service_request?.map(
        (item) => item?.single_services || 0
      ),
    },
  ];

  const handleSalesRevenueExportClick = () => {
    const revenueData = getServicesReportListData?.data?.service_request;

    if (!Array.isArray(revenueData) || revenueData.length === 0) {
      alert("No chart data available to export!");
      return;
    }

    const header = ["Month", "Recurring Services", "1 Time Services"];
    const rows = revenueData.map((item) => [
      `"${item?.month || "-"}"`,
      `"${item?.recurrig_services || 0}"`,
      `"${item?.single_services || 0}"`,
    ]);

    let csvContent =
      header.join(",") + "\n" + rows.map((row) => row.join(",")).join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "service_revenue_chart.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSalesBreakdownExportClick = () => {
    const salesData = getServicesReportListData?.data?.service_reports?.data;

    if (!Array.isArray(salesData) || salesData.length === 0) {
      alert("No sales data available to export!");
      return;
    }

    const header = [
      "No.",
      "Company Name",
      "Service ID",
      "Request Type",
      "Date",
    ];

    const rows = salesData.map((item, index) => [
      `"${index + 1}"`,
      `"${item?.company_name || "-"}"`,
      `"${item?.unique_id || "-"}"`,
      `"${item?.request_type || "-"}"`,
      `"${item?.date || "-"}"`,
    ]);

    let csvContent =
      header.join(",") + "\n" + rows.map((row) => row.join(",")).join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "service_breakdown.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const Revenue = () => {
    return (
      <div className="grid grid-cols-10 gap-6">
        {/* Pie Chart Box */}
        <div className="col-span-10 md:col-span-3 bg-white rounded-xl p-6 shadow border border-borderLine flex flex-col">
          <h2 className="text-xl font-normal font-Montserrat text-primaryBlacks">
            Service Type
          </h2>
          <p className="text-sm text-gray-dark mb-4">
            Total:{" "}
            <span className="font-bold text-lg">
              {getServicesReportListData?.data?.Total_services}
            </span>{" "}
            Service Request Number
          </p>
          <Chart
            options={PieChartOptions}
            series={pieSeries}
            type="pie"
            height={250}
          />
        </div>

        {/* Line Chart Box */}
        <div className="col-span-10 md:col-span-7 bg-white rounded-xl p-6 shadow border border-borderLine">
          <h2 className="text-xl font-normal font-Montserrat text-primaryBlacks">
            Servicing Type
          </h2>
          <p className="text-sm text-gray-dark mb-4">Types of Servicing</p>
          <Chart
            options={LineChartOptions}
            series={lineSeries}
            type="line"
            height={250}
          />
        </div>
      </div>
    );
  };

  const ColumnHeaders = () => (
    <>
      <th
        scope="col"
        className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start"
      >
        <span>No.</span>
      </th>
      <th
        scope="col"
        className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start"
      >
        <div
          className="flex items-center"
          onClick={() => {
            setSortBy("company_name");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          <span>Company</span>
          <SortDownArrow
            className={`ml-1 transition-transform duration-300 ${
              sortDirection === "asc" && sortBy === "company_name"
                ? "rotate-180"
                : ""
            }`}
          />
        </div>
      </th>
      <th className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start">
        <div
          className="flex items-center"
          onClick={() => {
            setSortBy("unique_id");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          Service Request Id
          <SortDownArrow
            className={`ml-1 transition-transform duration-300 ${
              sortDirection === "asc" && sortBy === "unique_id"
                ? "rotate-180"
                : ""
            }`}
          />
        </div>
      </th>
      <th
        scope="col"
        className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start"
      >
        <div
          className="flex items-center"
          onClick={() => {
            setSortBy("request_type");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          <span>Service Type</span>
          <SortDownArrow
            className={`ml-1 transition-transform duration-300 ${
              sortDirection === "asc" && sortBy === "request_type"
                ? "rotate-180"
                : ""
            }`}
          />
        </div>
      </th>
      <th
        scope="col"
        className="p-2 font-Proxima text-sm font-normal py-4 px-4 text-mgrey text-start"
      >
        <div
          className="flex items-center"
          onClick={() => {
            setSortBy("date");
            setSortDirection(sortDirection === "desc" ? "asc" : "desc");
          }}
        >
          <span>Date</span>
          <SortDownArrow
            className={`ml-1 transition-transform duration-300 ${
              sortDirection === "asc" && sortBy === "date" ? "rotate-180" : ""
            }`}
          />
        </div>
      </th>
    </>
  );

  const DataRows = () => {
    return getServicesReportListData?.data?.service_reports?.data?.map(
      (item, index) => (
        <tr
          key={item?.id || index}
          className="font-Proxima text-[14px] shadow-sm text-mgrey hover:bg-lightprimaryorange"
        >
          <td className="p-4 text-sm text-primaryBlack">{index + 1}</td>
          <td className="p-4 text-sm text-primaryBlack">
            {item?.company_name || "-"}
          </td>
          <td
            className="p-4 text-sm text-themeBtn underline cursor-pointer"
            onClick={() => navigate("/services")}
          >
            {item?.unique_id || "-"}
          </td>
          <td className="p-4 text-sm text-primaryBlack">
            {item?.request_type || "-"}
          </td>
          <td className="p-4 text-sm text-primaryBlack">{item?.date || "-"}</td>
        </tr>
      )
    );
  };

  const companyOptions = (companiesListData?.data || companiesListData)?.map(
    (companies) => ({
      value: companies.id,
      label: companies.name,
    })
  );

  const handleCompanyChange = (selectedOption) => {
    setCompany(selectedOption);
  };

  return (
    <div>
      <div className="flex justify-between items-start">
        <div>
          <h6 className="font-Montserrat font-medium text-xl mb-3">
            Service Reports
          </h6>
          <Breadcrumb
            breadCrumbParent="Home"
            breadCrumbParent2="Reports"
            breadCrumbActive="Service Report"
            breadCrumbTitleKey="/"
            classes=""
          />
        </div>
      </div>
      {loading ? (
        <RippleLoader />
      ) : (
        <>
          <div className="flex justify-between items-center mt-6">
            <h2 className="text-2xl font-normal font-Montserrat text-primaryBlacks">
              Service Report
            </h2>
            <div className="flex items-center gap-2">
              <div className="w-24">
                <SelectWithLabel
                  className="w-full py-2"
                  placeholder="Year"
                  options={yearOptions}
                  value={year}
                  onChange={handleYearChange}
                />
              </div>
              <ExportButton
                value="EXPORT CSV"
                className="-mt-1"
                onClick={handleSalesRevenueExportClick}
              />
            </div>
          </div>
          <div className="mt-6">
            <Revenue />
          </div>
          <div className="bg-white rounded-lg shadow p-6 border border-borderLine mt-6">
            <div>
              <h2 className="text-xl font-normal font-Montserrat text-primaryBlacks">
                Total Count:{" "}
                <span className="font-bold text-lg">
                  {
                    getServicesReportListData?.data?.service_reports
                      ?.total_services
                  }
                </span>
              </h2>
            </div>
            <div>
              <p className="text-sm text-gray-dark">
                Recurring:{" "}
                {
                  getServicesReportListData?.data?.service_reports
                    ?.recurrig_services
                }
              </p>
              <p className="text-sm text-gray-dark">
                1 Time:{" "}
                {
                  getServicesReportListData?.data?.service_reports
                    ?.single_services
                }
              </p>
            </div>
            <div className="mb-6 flex justify-between items-center">
              <h2 className="text-xl font-normal font-Montserrat text-primaryBlacks">
                Overview Breakdown
              </h2>
              <div className="flex flex-wrap items-center gap-2">
                {/* Company Dropdown */}
                <div className="w-full sm:w-[200px] md:w-[220px] -mt-1 min-w-[160px]">
                  <ReactSelect
                    className="w-full text-black p-0 font-Proxima"
                    id="company"
                    placeholder="Company"
                    options={companyOptions}
                    isClearable
                    getOptionValue={(option) => option?.value}
                    getOptionLabel={(option) => option?.label}
                    value={company}
                    onChange={handleCompanyChange}
                    styles={{
                      control: (base) => ({
                        ...base,
                        border: `1px solid #0000003B`,
                        padding: "1px",
                        borderColor: company ? "#F15B29" : "#0000003B",
                        borderRadius: "0.3rem",
                        boxShadow: "none",
                        "&:hover": {
                          border: "1px solid #0000003B",
                        },
                      }),
                      option: (base, state) => ({
                        ...base,
                        backgroundColor: state.isSelected ? "#F15B29" : "white",
                        "&:active": {
                          backgroundColor: state.isSelected
                            ? "#F15B29"
                            : "rgba(0, 0, 0, 0.04)",
                        },
                      }),
                    }}
                  />
                </div>

                {/* Date Range Picker */}
                <div className="w-full sm:w-[220px] md:w-[240px] -mt-1 min-w-[160px]">
                  <div className="text-black border border-borderTheme/80 flex justify-between relative rounded w-full px-2 py-[7px] items-center transition duration-300 ease-in-out">
                    <ReactDatePicker
                      selected={dateRange[0]}
                      onChange={(dates) => setDateRange(dates)}
                      selectsRange
                      isClearable
                      startDate={dateRange[0]}
                      endDate={dateRange[1]}
                      maxDate={new Date()}
                      dateFormat="dd/MM/yyyy"
                      placeholderText="Date Range"
                      className="w-full"
                    />
                  </div>
                </div>

                {/* Export Button */}
                <div className="w-full sm:w-auto -mt-1">
                  <ExportButton
                    value="EXPORT CSV"
                    onClick={handleSalesBreakdownExportClick}
                  />
                </div>
              </div>
            </div>
            <CustomTable
              overflowVisibleLg={true}
              columnHeaders={<ColumnHeaders />}
              dataRows={<DataRows />}
              data={getServicesReportListData?.data?.service_reports}
              loading={false}
              showPagination={true}
              setPaginationCurrentLimit={setPageLimit}
              paginationCurrentLimit={pageLimit}
              current={pageCount}
              total={getServicesReportListData?.data?.service_reports?.total}
              paginationData={(crPage) => setPageCount(crPage)}
              lastPage={Math.ceil(
                getServicesReportListData?.data?.service_reports?.total /
                  pageLimit
              )}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default ServiceRequestReports;
